import React from 'react';

/**
 * Fallback UI rendered by the root `Sentry.ErrorBoundary` (see App.tsx) when an
 * uncaught render error bubbles up. It deliberately renders WITHOUT any of the
 * app context providers (Auth/User/Branding/etc.) so it still works when the
 * crash originated inside a provider itself. Sentry receives the error report
 * automatically via the boundary; this component only handles recovery UX.
 *
 * Actions are plain browser navigation (no router/provider dependency):
 *  - "Reload page" re-runs the whole app from a clean state.
 *  - "Back to dashboard" sends the user to a known-good route.
 */
function ErrorFallback() {
  const handleReload = () => {
    window.location.reload();
  };

  const handleBackToDashboard = () => {
    window.location.href = '/dashboard';
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
      <div className="absolute left-8 top-8">
        <img src="/TachLogo.svg" alt="TACH Logo" className="h-6" />
      </div>

      <div className="flex max-w-md flex-col items-center text-center">
        <h1 className="mb-2 text-4xl font-bold text-gray-900">Uh oh.</h1>
        <h1 className="mb-6 text-4xl font-bold text-gray-900">
          Something went wrong!
        </h1>
        <p className="mb-8 text-gray-600">
          An unexpected error occurred. Our team has been notified. Please try
          reloading the page.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            onClick={handleReload}
            className="w-fit rounded-full bg-accent px-8 py-3 font-medium text-white transition-colors hover:bg-orange-500"
          >
            Reload page
          </button>
          <button
            onClick={handleBackToDashboard}
            className="w-fit rounded-full border border-gray-300 px-8 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorFallback;
